// Session Logging and Adaptive Feedback System
import { saveSessionToFirebase, getSessionHistoryFromFirebase } from './firebaseSync';
import { getCurrentUserId, withAuth } from './anonymousAuth';
import { RangeDrillLog, RangeSessionLog } from './rangeProtocols';
import { roundToNearestPlate } from './plateMath';
import { resolveExerciseName, isNonExerciseLabel } from './exerciseDatabase';
import { getUserProfile, updateUserProfile, createDefaultProfile, saveUserProfile } from './userProfileService';
import { analyzeUserProfile } from './profileLearning';
import { updateFitnessFatigue } from './fitnessFatigueModel';
import { db, DBSession, stampTotalVolume } from './db';
import { submitActiveOperationContribution } from './communityOperations';
import { awardIntelForSession } from './xpSystem';
import { storage } from './storage';

// ============================================================================
// SESSION CACHE - Prevents redundant Dexie queries within a page load
// ============================================================================
interface SessionCache {
  data: SessionLog[] | null;
  userId: string | null;
  since: Date | null;
  timestamp: number;
}

const sessionCache: SessionCache = {
  data: null,
  userId: null,
  since: null,
  timestamp: 0,
};

// Cache TTL in milliseconds
// Set to 5 minutes so all recommendation engines during a page load
// hit the cache instead of making 40+ redundant IndexedDB queries.
// Cache is explicitly invalidated on session save, so stale data is not a concern.
const SESSION_CACHE_TTL = 300000;

/**
 * Invalidate the session cache. Call this when sessions are modified.
 */
export function invalidateSessionCache(): void {
  sessionCache.data = null;
  sessionCache.userId = null;
  sessionCache.since = null;
  sessionCache.timestamp = 0;
}

/**
 * Set kind. Tags the *role* of a set so downstream analytics know not
 * to treat e.g. a drop set as the user getting weaker.
 *
 *   'normal'     — straight working set (default when omitted)
 *   'warmup'     — ramping set, lighter than working weight
 *   'drop'       — drop set immediately after a heavier set
 *   'rest-pause' — mini-rest cluster after a fail point
 *   'myo-rep'    — myo-rep activation set's follow-up clusters
 *   'amrap'      — final all-out set; reps target is "as many as possible"
 *
 * ML integration:
 *   - Epley e1RM (lib/dynamic1RM) skips kind !== 'normal'
 *   - Auto-progression (lib/autoProgress) considers only top 'normal' set for RPE
 *   - Intra-session RPE gate (lib/intraSessionRPE) skips drop / rest-pause / myo-rep
 *   - PR detection (V2 + WorkoutSummary) skips kind !== 'normal'
 *   - Volume tracking (lib/volumeTracking) INCLUDES all kinds — they are real work
 *   - Statistical recommendation excludes non-normal from RPE-to-load ratios
 */
export type SetKind = 'normal' | 'warmup' | 'drop' | 'rest-pause' | 'myo-rep' | 'amrap';

export interface SetLog {
  weight: number;
  // Optional display token for bodyweight movements (e.g. "BW"). Keep weight=0 when set.
  weightLabel?: 'BW';
  reps: number;
  rpe: number | null;
  completed: boolean;
  /** The weight the engine RECOMMENDED for this set at workout generation
   *  (the seeded value before the user edited it). Stored next to the actual
   *  `weight` so we can audit recommended-vs-actual and tell whether the
   *  recommendation pipeline tracks real performance. Omitted when unknown
   *  (e.g. exercises the user added mid-session). */
  recommendedWeight?: number;
  /** The reps the engine recommended for this set (seeded prescription),
   *  stored next to actual `reps` for the recommendation-vs-actual audit. */
  recommendedReps?: number;
  /** V2-captured rest duration in seconds — interval between user tapping
   *  Done on this set and Go on the next. Fed into lib/restPaceLearning. */
  actualRestSeconds?: number;
  /** Set role. Defaults to 'normal' when omitted — older records without
   *  this field are treated as normal sets across all consumers. */
  kind?: SetKind;
  /** True when the set was performed per-side (Bulgarian split squat,
   *  single-arm DB row, pistol squat). `reps` is the per-side count;
   *  volume tracking doubles to count both sides. Optional `rightReps`
   *  captures asymmetric work (e.g. 10 on the strong side, 8 on the
   *  weak); when omitted, both sides assumed equal at `reps`. */
  unilateral?: boolean;
  rightReps?: number;
  /** Two dumbbells, one per hand, moving together (DB bench, Arnold press).
   *  The logged `weight` is PER HAND, so true volume is weight × reps × 2.
   *  Stamped from isPerHandLoad() at generation time so history self-describes
   *  without re-inferring from the name. Mutually exclusive with `unilateral`
   *  (that path already doubles via per-side reps). */
  perHand?: boolean;
  /** Mid-set weight drops within a single continuous set. Real drop-
   *  set semantics: target 15 reps at 50 lb, fail at 11, immediately
   *  drop to 40 lb for 4 more — that's one set with two segments.
   *  The first segment is encoded in `weight` and `reps` above; this
   *  array captures any subsequent drops in the order they occurred.
   *  Total reps for the set = reps + sum(dropSegments.reps).
   *  Total volume = weight*reps + sum(seg.weight * seg.reps).
   *  e1RM / PR use only the first segment (heaviest = strength).
   */
  dropSegments?: Array<{ weight: number; reps: number }>;
  /** Average BPM across this set's working time, captured from a paired
   *  BLE HR monitor. Undefined when no device was paired or no signal
   *  arrived during the set. */
  avgHr?: number;
  /** Peak BPM seen during this set. Useful for surface analytics:
   *  "this set spiked you to Z5 even though target was Z3 RPE 7". */
  maxHr?: number;
  /** Heart Rate Recovery (BPM drop during the FIRST 60s of the rest that
   *  followed this set). Cole 1999 NEJM established HRR-60 as a robust
   *  autonomic recovery signal; we use it both for live "Ready" detection
   *  in the V2 rest UI and as an offline input to rest-pace ML. Undefined
   *  when no HR was streaming, or when the user advanced before 60s of
   *  rest had elapsed. */
  hrrDropBpm?: number;
  /** Seconds it took post-set HR to drop below 60% of estimated HRmax —
   *  the Z2 boundary, used as our "fully recovered" threshold. Null in
   *  the HrrSample type means it never crossed during the captured rest,
   *  which on the wire is encoded as the field being absent. */
  hrrSecsToReady?: number;
  /** Seconds spent in each HR zone DURING the working portion of this
   *  set. Captured from the BLE HR buffer in finishSet. Rolled up to
   *  the session level (PTSessionLog.zoneSecs) on save so the Z2
   *  weekly accumulator can read it. Absent when no BLE HR was paired. */
  zoneSecs?: { z1: number; z2: number; z3: number; z4: number; z5: number };
  /**
   * Duration of a TIMED set, in seconds.
   *
   * 155 exercises in the DB are trackingType 'time' — planks, holds, dead
   * hangs, wall sits, carries measured by clock. Until this field existed
   * there was NOWHERE to record how long one was actually held: the live set
   * carries `time` as "MM:SS" and the user types it in, and the save mapper
   * had no slot to put it in, so it was dropped on Finish.
   *
   * The consequence was that a plank could not trend. Every timed exercise
   * logged before this looks identical in history regardless of whether the
   * user held it for 20 seconds or two minutes — and the reps column was
   * being used as a stand-in, which is why timed work showed up as a
   * rep-counted movement in analytics.
   *
   * Stored as a NUMBER of seconds rather than the display string so it can be
   * summed, averaged and trended without reparsing.
   */
  durationSec?: number;
  /**
   * Distance covered in this set, in METRES.
   *
   * Written by the cardio/ruck bout set and by circuit members that are
   * carries. It was already being persisted through an `as any` cast without
   * being declared here, which meant no consumer could read it type-safely —
   * exactly the kind of undeclared-but-present field that makes a schema
   * untrustworthy. Declared so readers (pace-under-load, carry volume) can use
   * it directly.
   *
   * NOT written by the standard sets-reps mapper: a weighted set has no
   * distance, and carries store metres in `reps` there for legacy
   * compatibility.
   */
  distance?: number;
}

export interface SwimSetLog {
  distance: number;   // meters
  time: string;       // MM:SS format
  rpe: number | null;
  completed: boolean;
}

export interface CircuitSetLog {
  round: number;      // round number
  time: string;       // MM:SS format (total round time)
  rpe: number | null;
  completed: boolean;
  runTime?: string;   // MM:SS format - sprint/run time within the round (for cardio ML)
  weight?: number;    // Pounds - for weighted elements (sled, ruck, carries)
  /** Metres covered in this round. The writer has always emitted this
   *  (WorkoutDisplayV2's circuitSets mapping) but the type omitted it, so
   *  every reader was type-blind to a field that was sitting in the data.
   *  It is the progression lever for a carry inside a circuit block. */
  distance?: number;
  /** CrossFit scaled-vs-RX tag. Optional. When true, this round was
   *  performed at scaled loads/reps and shouldn't be pooled with RX
   *  data in adaptive recommendations. */
  scaled?: boolean;
}

// ============================================================================
// CIRCUIT BLOCK LOG - Complete circuit tracking for ML/adaptive learning
// ============================================================================

export interface CircuitExerciseInfo {
  name: string;
  targetReps: number | string;  // e.g., 10 or "AMRAP"
  targetTime?: string;          // e.g., "0:30" for timed exercises
}

export interface CircuitBlockLog {
  blockName: string;                    // e.g., "Grinder PT Circuit"
  blockFormat: string;                  // e.g., "circuit", "amrap", "emom", "for-time"
  exercises: CircuitExerciseInfo[];     // List of exercises in the circuit
  targetRounds: number;                 // Target number of rounds
  completedRounds: number;              // Actual rounds completed
  extraReps?: number;                   // For AMRAP: partial round reps
  totalTime?: string;                   // For "for time" circuits: MM:SS
  rpe: number | null;                   // Overall circuit RPE
  totalVolume: number;                  // Calculated total reps
  date: Date;                           // When this circuit was performed
  week?: number;
  day?: number;
}

/**
 * Calculate circuit volume (total reps across all rounds)
 */
export function calculateCircuitVolume(
  exercises: CircuitExerciseInfo[],
  completedRounds: number,
  extraReps?: number
): number {
  // Sum up target reps per round
  const repsPerRound = exercises.reduce((sum, ex) => {
    if (typeof ex.targetReps === 'number') {
      return sum + ex.targetReps;
    }
    // For "AMRAP" or string reps, estimate based on common values
    const parsed = parseInt(String(ex.targetReps));
    return sum + (isNaN(parsed) ? 10 : parsed); // Default to 10 if unparseable
  }, 0);

  return (completedRounds * repsPerRound) + (extraReps || 0);
}

/**
 * Parse time string (MM:SS) to seconds
 */
export function parseTimeToSeconds(time: string): number {
  if (!time) return 0;
  const parts = time.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  return parseInt(time) || 0;
}

/**
 * Get circuit history for a specific block name
 */
export async function getCircuitHistory(blockName: string): Promise<CircuitBlockLog[]> {
  const sessions = await getSessionHistory();
  const history: CircuitBlockLog[] = [];
  const normalizedBlockName = blockName.toLowerCase().trim();

  sessions
    .filter((s): s is PTSessionLog => s.sessionType === 'pt' || s.sessionType === 'assessment')
    .forEach(session => {
      session.exercises
        .filter(ex => ex.exerciseType === 'circuit' && ex.completed)
        .forEach(ex => {
          const exBlockName = (ex.block || ex.exerciseName || '').toLowerCase().trim();

          // Match by block name or exercise name
          if (exBlockName.includes(normalizedBlockName) || normalizedBlockName.includes(exBlockName)) {
            // Build circuit info from exercise data
            const circuitLog: CircuitBlockLog = {
              blockName: ex.block || ex.exerciseName,
              blockFormat: ex.format || 'circuit',
              exercises: [], // We don't have per-exercise breakdown in current storage
              targetRounds: parseInt(ex.targetSets?.match(/^(\d+)/)?.[1] || '4'),
              completedRounds: ex.actualRounds || 0,
              extraReps: undefined,
              totalTime: ex.actualTime,
              rpe: ex.actualRPE || null,
              totalVolume: 0, // Will be calculated if we have exercise breakdown
              date: session.date,
              week: session.week,
              day: session.day,
            };
            history.push(circuitLog);
          }
        });
    });

  // Sort by date descending (most recent first)
  return history.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Analyze circuit performance and generate recommendations
 */
export interface CircuitRecommendation {
  blockName: string;
  recommendation: 'increase_rounds' | 'decrease_rounds' | 'maintain' | 'increase_intensity' | 'add_weight';
  reason: string;
  suggestedRounds?: number;
  confidence: number;
}

export async function analyzeCircuitPerformance(
  blockName: string,
  currentTargetRounds: number
): Promise<CircuitRecommendation | null> {
  const history = await getCircuitHistory(blockName);

  if (history.length < 2) {
    return null; // Need at least 2 data points
  }

  const recent = history.slice(0, 5); // Last 5 performances
  const avgRPE = recent.reduce((sum, h) => sum + (h.rpe || 7), 0) / recent.length;
  const avgCompletionRate = recent.reduce((sum, h) => sum + (h.completedRounds / h.targetRounds), 0) / recent.length;

  // Check for improvement trend (comparing first half vs second half)
  const firstHalf = recent.slice(0, Math.ceil(recent.length / 2));
  const secondHalf = recent.slice(Math.ceil(recent.length / 2));

  const firstHalfAvgRounds = firstHalf.reduce((sum, h) => sum + h.completedRounds, 0) / firstHalf.length;
  const secondHalfAvgRounds = secondHalf.reduce((sum, h) => sum + h.completedRounds, 0) / secondHalf.length;
  const improving = secondHalfAvgRounds > firstHalfAvgRounds;

  // Generate recommendation
  if (avgRPE >= 9 && avgCompletionRate < 0.9) {
    return {
      blockName,
      recommendation: 'decrease_rounds',
      reason: `High average RPE (${avgRPE.toFixed(1)}) and incomplete rounds (${(avgCompletionRate * 100).toFixed(0)}% completion). Consider reducing target rounds.`,
      suggestedRounds: Math.max(1, currentTargetRounds - 1),
      confidence: 0.85,
    };
  }

  if (avgRPE <= 6 && avgCompletionRate >= 1.0 && improving) {
    return {
      blockName,
      recommendation: 'increase_rounds',
      reason: `Low RPE (${avgRPE.toFixed(1)}) and consistently completing all rounds. Progress by adding rounds.`,
      suggestedRounds: currentTargetRounds + 1,
      confidence: 0.8,
    };
  }

  if (avgRPE >= 7 && avgRPE <= 8 && avgCompletionRate >= 0.95) {
    return {
      blockName,
      recommendation: 'maintain',
      reason: `Good challenge level (RPE ${avgRPE.toFixed(1)}, ${(avgCompletionRate * 100).toFixed(0)}% completion). Maintain current programming.`,
      confidence: 0.9,
    };
  }

  if (avgRPE < 7 && avgCompletionRate >= 1.0) {
    return {
      blockName,
      recommendation: 'increase_intensity',
      reason: `Circuit feels easy (RPE ${avgRPE.toFixed(1)}). Consider faster transitions or harder exercise variations.`,
      confidence: 0.75,
    };
  }

  return {
    blockName,
    recommendation: 'maintain',
    reason: `Performance is stable. Continue with current programming.`,
    confidence: 0.6,
  };
}

export interface SupersetExerciseLog {
  name: string;
  label: string;      // "A1", "A2", etc.
  weight: number;
  reps: number;
  rpe: number | null;
  targetLoad: number;
  completed: boolean;
}

export interface SupersetRoundLog {
  round: number;      // round number (1, 2, 3, etc.)
  exercises: SupersetExerciseLog[];
  completed: boolean;
}

export interface IntervalSetLog {
  distance: number;   // Distance covered in meters during the work interval
  completed: boolean;
}

export interface ExerciseLog {
  exerciseName: string;
  exerciseType: 'weight' | 'bodyweight' | 'cardio' | 'timed' | 'circuit' | 'ruck' | 'swim-sets' | 'carry' | 'interval';
  block: string;
  format: string;
  targetSets: string;
  targetLoad: number;
  targetRPE: string;
  restPeriod: string;
  completed: boolean;
  // Per-set tracking for weight-based exercises
  sets?: SetLog[];
  // Per-set tracking for swim intervals
  swimSets?: SwimSetLog[];
  // Per-round tracking for circuits
  circuitSets?: CircuitSetLog[];
  // Per-round tracking for supersets
  supersetRounds?: SupersetRoundLog[];
  // Per-interval tracking for HIIT sprints (30/60s, 60/120s)
  intervalSets?: IntervalSetLog[];
  /**
   * Prescribed effort target as RIR (reps in reserve). Structured counterpart
   * to the `targetRPE` prose string, which is an empty string in 1206 of 1303
   * production logs and therefore unparseable in the general case.
   *
   * lib/patternMemory.ts scored RPE compliance against this via an `as any`
   * read for a long time while NO writer emitted it, leaving ~35% of the
   * session-quality score permanently without an input. Persisted since
   * 2026-08-11; older logs legitimately lack it.
   */
  rirTarget?: number | null;
  // Legacy single-value tracking (for backward compatibility and non-weight exercises)
  actualRPE?: number;
  actualLoad?: number;      // Weight-based exercises
  actualReps?: number;       // Bodyweight exercises
  actualTime?: string;       // Timed exercises (mm:ss format)
  actualDistance?: number;   // Cardio distance (meters)
  actualIncline?: number;    // Treadmill incline percentage (0-15%)
  actualRounds?: number;     // Circuits/AMRAPs
  /**
   * Daniels VDOT for a qualifying run (>= 1 mi, >= 5 min).
   *
   * Derived from distance and time alone — no heart-rate strap required — so
   * it is the one aerobic-fitness number the app can produce for every runner
   * today. It was already being computed and rendered on the run-finish screen
   * and then discarded, which meant runners had pace history but no fitness
   * trajectory. Absent for rucks, bikes, rows and short runs.
   */
  estimatedVdot?: number;
  notes?: string;
  /** Recommendation audit trail — the WHY behind the prescribed load, frozen
   *  at workout generation. Pairs with each set's recommendedWeight/Reps so
   *  we can analyze recommendation-vs-actual AND its rationale over time
   *  (e.g. "recommended 230 at 88% confidence from 12 sessions → did 225").
   *  Embedded in the session doc — no extra Firestore writes. */
  recommendationReasoning?: string;
  recommendationConfidence?: number;  // 0-1
  recommendationDataPoints?: number;  // sessions the rec was based on
  recommendationSource?: string;      // 'statistical' | 'adjusted' | 'program'
  /** Prescribed tempo, e.g. "3-0-1-0" (eccentric-pause-concentric-pause).
   *  Display-only for now. Future ML use: TUT-weighted volume and
   *  tempo-corrected e1RM so a 4-second-eccentric squat isn't pooled
   *  with a touch-and-go squat for progression analysis. */
  tempo?: string;
  /** Per-exercise bar weight override (lb). When omitted, uses the
   *  user's default from PlateConfig. Only meaningful for barbell
   *  movements. */
  barWeight?: number;
}

// Base session properties
interface BaseSessionLog {
  id: string;
  userId: string;
  programSelection?: string; //  NEW: Program ID (e.g., "operator-13-week-operator--1761677409190")
  week: number;
  day: number;
  date: Date;
  startTime: Date;
  endTime?: Date;
  /**
   * SECONDS for PT/assessment/sprint sessions, MINUTES for Range sessions.
   *
   * Two units in one field. Consumers that sum or format across session types
   * treat everything as seconds, so a 45-minute range session reads as 45
   * seconds. Do NOT read this directly when mixing types — use
   * `sessionDurationSeconds()` below.
   */
  duration: number;
  overallFatigue: number; // 1-10
  readiness: number; // from recovery tracker
  notes?: string;
  isDeload?: boolean; // true when session was generated with deload adjustments
}

// PT Session (existing)
/**
 * Intra-session RPE-gate telemetry. Captured when V2's gate fires a
 * weight-change suggestion the user can Accept or Dismiss. Feeds into
 * downstream auto-progression: a user who consistently dismisses
 * suggestions on a given lift is signalling their RPE shouldn't drive
 * load reductions on it.
 */
export interface GateInteraction {
  exerciseName: string;
  setIdx: number;
  /** 'reanchor' = the load-deviation classifier read a completed set as a
   *  capacity mismatch (beat the rec with room to spare) and offered to move
   *  the remaining sets to the demonstrated load. weightAdjustment carries
   *  demonstrated/prescribed (e.g. 1.31 = did 31% over the rec). */
  action: 'reduce-weight' | 'increase-weight' | 'stop' | 'reanchor';
  weightAdjustment: number; // 0.95 = -5%, 1.05 = +5%, 0 for 'stop' actions
  accepted: boolean;
  timestamp: number;
}

export interface PTSessionLog extends BaseSessionLog {
  sessionType: 'pt' | 'assessment';
  exercises: ExerciseLog[];
  /**
   * BUILD STAMP — which app and which prescription engine produced this
   * session. Audit item 2.2, reported done in Phase 2 and in fact never
   * implemented: a session saved 2026-08-15 carried neither field.
   *
   * Without them, any before/after comparison of an engine change silently
   * pools old-engine and new-engine records, so a regression can hide inside
   * the average. Optional because every session logged before 2026-08-15
   * lacks them, and those records are still perfectly valid — absence just
   * means "pre-stamp".
   */
  appVersion?: string;
  engineVersion?: number;
  /** Optional. Present only when the gate fired at least once this session. */
  gateInteractions?: GateInteraction[];
  /** Aggregated HR zone seconds for the whole session — sum of every
   *  set's zoneSecs. Present when BLE HR was paired and at least one
   *  set captured samples. Read by lib/zoneMinutes weekly accumulator
   *  as a fallback when no wearable daily HR exists for the date. */
  zoneSecs?: { z1: number; z2: number; z3: number; z4: number; z5: number };
}

// Range Session (new)
export interface RangeSession extends BaseSessionLog {
  sessionType: 'range';
  protocolId: string;
  protocolName: string;
  drills: RangeDrillLog[];
  totalRounds: number;
  overallAccuracy: number;
  cost?: number;
  highlights: {
    newPRs: string[];
    perfectRuns: string[];
    improvements: string[];
  };
}

// Sprint Session (new)
export interface SprintSession extends BaseSessionLog {
  sessionType: 'sprint';
  exercises: ExerciseLog[]; // Uses standard exercise log format
  totalVolume?: number; // total distance in meters
  averageRPE?: number;
}

// Unified session type
export type SessionLog = PTSessionLog | RangeSession | SprintSession;

export interface AdaptiveRecommendation {
  exercise: string;
  reason: string;
  adjustment: 'increase' | 'decrease' | 'maintain' | 'deload';
  suggestedLoad: number;
  confidence: number; // 0-1
}

/**
 * Analyze session data and provide adaptive recommendations
 * Only works for PT sessions - range sessions don't use this system
 */
export function analyzeSession(log: SessionLog): AdaptiveRecommendation[] {
  const recommendations: AdaptiveRecommendation[] = [];

  // Range sessions don't use adaptive recommendations
  if (log.sessionType === 'range') {
    return recommendations;
  }

  log.exercises.forEach(ex => {
    // Handle per-set tracking or legacy single-value
    const avgRPE = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((sum, set) => sum + (set.rpe || 0), 0) / ex.sets.filter(s => s.rpe !== null).length
      : ex.actualRPE || 0;
    
    const avgLoad = ex.sets && ex.sets.length > 0
      ? ex.sets.reduce((sum, set) => sum + set.weight, 0) / ex.sets.length
      : ex.actualLoad || 0;

    if (!ex.completed || avgRPE === 0 || avgLoad === 0) return;

    const targetRPENum = parseRPE(ex.targetRPE);
    const rpeDiff = avgRPE - targetRPENum;
    const loadDiff = avgLoad - ex.targetLoad;

    let adjustment: 'increase' | 'decrease' | 'maintain' | 'deload' = 'maintain';
    let suggestedLoad = ex.targetLoad;
    let reason = '';
    let confidence = 0.7;

    // RPE-based logic
    if (avgRPE >= 9.5) {
      // Too hard - reduce load
      adjustment = 'decrease';
      suggestedLoad = roundToNearestPlate(avgLoad * 0.95);
      reason = `Avg RPE ${avgRPE.toFixed(1)} too high (target: ${targetRPENum}). Reduce load 5%.`;
      confidence = 0.9;
    } else if (avgRPE <= 6 && targetRPENum >= 7) {
      // Too easy - increase load
      adjustment = 'increase';
      suggestedLoad = roundToNearestPlate(avgLoad * 1.05);
      reason = `Avg RPE ${avgRPE.toFixed(1)} too low (target: ${targetRPENum}). Increase load 5%.`;
      confidence = 0.85;
    } else if (Math.abs(rpeDiff) <= 0.5) {
      // On target
      adjustment = 'maintain';
      suggestedLoad = roundToNearestPlate(avgLoad);
      reason = `Avg RPE ${avgRPE.toFixed(1)} on target. Continue with current load.`;
      confidence = 0.95;
    } else if (rpeDiff > 1) {
      // Moderately too hard
      adjustment = 'decrease';
      suggestedLoad = roundToNearestPlate(avgLoad * 0.975);
      reason = `Avg RPE ${avgRPE.toFixed(1)} slightly high. Small reduction recommended.`;
      confidence = 0.8;
    } else if (rpeDiff < -1) {
      // Moderately too easy
      adjustment = 'increase';
      suggestedLoad = roundToNearestPlate(avgLoad * 1.025);
      reason = `Avg RPE ${avgRPE.toFixed(1)} slightly low. Small increase recommended.`;
      confidence = 0.8;
    }

    // Fatigue override
    if (log.overallFatigue >= 8) {
      adjustment = 'deload';
      suggestedLoad = roundToNearestPlate(ex.targetLoad * 0.9);
      reason = `High fatigue (${log.overallFatigue}/10). Recommend deload week.`;
      confidence = 0.95;
    }

    // Readiness override
    if (log.readiness < 70) {
      adjustment = 'decrease';
      suggestedLoad = roundToNearestPlate(ex.targetLoad * 0.9);
      reason = `Low readiness (${log.readiness}/100). Reduce volume/intensity.`;
      confidence = 0.9;
    }

    recommendations.push({
      exercise: ex.exerciseName,
      reason,
      adjustment,
      suggestedLoad,
      confidence,
    });
  });

  return recommendations;
}

/**
 * Parse RPE string to number (handles ranges like "7-8")
 */
function parseRPE(rpeStr: string): number {
  if (!rpeStr || rpeStr === 'nan') return 7.5; // default
  
  const match = rpeStr.match(/(\d+(?:\.\d+)?)/);
  if (!match) return 7.5;
  
  return parseFloat(match[1]);
}

/**
 * Calculate if user needs deload based on session history
 */
export function detectDeloadNeed(recentSessions: SessionLog[]): {
  needsDeload: boolean;
  reason: string;
  confidence: number;
} {
  if (recentSessions.length < 3) {
    return { needsDeload: false, reason: 'Not enough data', confidence: 0 };
  }

  const last3Sessions = recentSessions.slice(-3);
  
  // Check for consistent high RPE (handle per-set or legacy)
  const avgRPE = last3Sessions.reduce((sum, s) => {
    if (s.sessionType === 'range' || !s.exercises) return sum + 7;
    
    const sessionAvgRPE = s.exercises.reduce((eSum, ex) => {
      const exRPE = ex.sets && ex.sets.length > 0
        ? ex.sets.reduce((setSum, set) => setSum + (set.rpe || 0), 0) / ex.sets.filter(s => s.rpe !== null).length
        : ex.actualRPE || 0;
      return eSum + exRPE;
    }, 0) / s.exercises.length;
    return sum + sessionAvgRPE;
  }, 0) / last3Sessions.length;

  if (avgRPE >= 9) {
    return {
      needsDeload: true,
      reason: `Average RPE ${avgRPE.toFixed(1)} across last 3 sessions. Body needs recovery.`,
      confidence: 0.9,
    };
  }

  // Check for declining performance (only for weight-based exercises)
  const ptOnly = last3Sessions.filter((s): s is PTSessionLog => 
    (s.sessionType === 'pt' || s.sessionType === 'assessment') && !!s.exercises
  );
  const loads = ptOnly.flatMap(s => 
    s.exercises
      .filter(ex => ex.actualLoad && ex.targetLoad > 0)
      .map(ex => (ex.actualLoad || 0) / ex.targetLoad)
  );
  if (loads.length === 0) {
    return { needsDeload: false, reason: 'Not enough weight-based exercises', confidence: 0 };
  }
  const avgLoadPerformance = loads.reduce((a, b) => a + b, 0) / loads.length;

  if (avgLoadPerformance < 0.9) {
    return {
      needsDeload: true,
      reason: `Performance declining (avg ${(avgLoadPerformance * 100).toFixed(0)}% of target). Deload recommended.`,
      confidence: 0.85,
    };
  }

  // Check readiness
  const avgReadiness = last3Sessions.reduce((sum, s) => sum + s.readiness, 0) / last3Sessions.length;
  
  if (avgReadiness < 65) {
    return {
      needsDeload: true,
      reason: `Low readiness (${avgReadiness.toFixed(0)}/100) for ${last3Sessions.length} sessions. Body needs rest.`,
      confidence: 0.95,
    };
  }

  return { needsDeload: false, reason: 'Recovery status good', confidence: 0.8 };
}

/**
 * Update 1RM estimates based on performance
 */
export function suggestOneRMUpdate(
  exerciseName: string,
  recentLogs: ExerciseLog[]
): { shouldUpdate: boolean; suggestedRM: number; reason: string } | null {
  if (recentLogs.length < 3) return null;

  // Look for consistent performance at high reps
  const goodPerformance = recentLogs.filter(log => {
    const reps = parseInt(log.targetSets.split('x')[1] || '5');
    const avgRPE = log.sets && log.sets.length > 0
      ? log.sets.reduce((sum, set) => sum + (set.rpe || 0), 0) / log.sets.filter(s => s.rpe !== null).length
      : log.actualRPE || 10;
    return avgRPE <= 8 && reps <= 5 && log.completed;
  });

  if (goodPerformance.length >= 2) {
      // Calculate potential new 1RM using Brzycki formula
      const latestGood = goodPerformance[goodPerformance.length - 1];
      const reps = parseInt(latestGood.targetSets.split('x')[1] || '5');
      
      // Use average weight from sets if available
      const weight = latestGood.sets && latestGood.sets.length > 0
        ? latestGood.sets.reduce((sum, set) => sum + set.weight, 0) / latestGood.sets.length
        : latestGood.actualLoad || 0;
      
      if (weight === 0) return null;
      const estimated1RM = Math.round(weight / (1.0278 - 0.0278 * reps));
      
      const avgRPE = latestGood.sets && latestGood.sets.length > 0
        ? latestGood.sets.reduce((sum, set) => sum + (set.rpe || 0), 0) / latestGood.sets.filter(s => s.rpe !== null).length
        : latestGood.actualRPE || 7;

    return {
      shouldUpdate: true,
      suggestedRM: estimated1RM,
      reason: `Completed ${latestGood.targetSets} @ ${weight.toFixed(0)}lbs with RPE ${avgRPE.toFixed(1)}. Estimated 1RM: ${estimated1RM}lbs.`,
    };
  }

  return null;
}

/**
 * Save session to Firebase AND localStorage
 * Firebase is primary, localStorage is backup/cache
 * Returns status object for UI feedback
 */
export async function saveSession(log: SessionLog): Promise<{ savedLocally: boolean; savedToCloud: boolean; error?: string }> {
  let savedLocally = false;
  let savedToCloud = false;
  let error: string | undefined;

  // CANONICALIZE AT SOURCE — collapse spelling variants to one canonical name
  // and drop non-exercise block labels ("Superset D") so history/recommendations
  // never fragment again. Single chokepoint for every save path (V2, V1,
  // freestyle, templates). Only touches PT/assessment logs that carry exercises.
  if ((log.sessionType === 'pt' || log.sessionType === 'assessment') && Array.isArray((log as any).exercises)) {
    (log as any).exercises = (log as any).exercises
      .filter((e: any) => e && !isNonExerciseLabel(e.exerciseName))
      .map((e: any) => ({ ...e, exerciseName: resolveExerciseName(e.exerciseName) }));
  }

  // Reentry ramp progression — bump session count when a real training
  // session completes under an accepted plan. Session-count-based
  // progression replaces the old calendar-day logic (Apr 2026) so a 1-week
  // ramp can't "complete" the moment a user logs their first workout.
  // Fire-and-forget: don't block save on a non-critical telemetry write.
  if (log.sessionType === 'pt' || log.sessionType === 'assessment') {
    (async () => {
      try {
        const uid = getCurrentUserId();
        if (!uid) return;
        const { recordRampSession } = await import('./acceptedReentryPlan');
        await recordRampSession(uid);
      } catch { /* non-critical */ }
    })();
  }

  // Damper outcome attribution — pair the damper snapshot captured at
  // session generation (stashed via unifiedRecommendation) with the
  // session quality scored from the persisted session log. Persisted
  // outcomes feed lib/damperCalibration so per-user thresholds replace
  // population defaults once ~15 outcomes accumulate. Also triggers a
  // recalibration write when the new outcome pushes us past the
  // shouldRecalibrate gate. Fire-and-forget; non-critical.
  if (log.sessionType === 'pt') {
    (async () => {
      try {
        const uid = getCurrentUserId();
        if (!uid) return;
        const ptLog = log as PTSessionLog;
        const { consumeSnapshot, scoreSessionOutcome, recordDamperOutcome, loadDamperOutcomes } = await import('./damperOutcomes');
        const snapshot = consumeSnapshot('__pending__');
        if (!snapshot) return; // No dampers ran (anonymous user, no personal state, etc.)
        const outcome = await scoreSessionOutcome(ptLog);
        if (!outcome) return; // Session too sparse to score.
        await recordDamperOutcome(uid, ptLog, snapshot, outcome);

        // Calibration trigger — cheap check + write when warranted. Loads
        // all outcomes (incl. the one we just wrote) and decides whether
        // the new datapoint pushed us past a threshold. Most session
        // saves no-op here.
        const { loadPersonalDamperThresholds, savePersonalDamperThresholds, calibrateUserThresholds, shouldRecalibrate } = await import('./damperCalibration');
        const [current, outcomes] = await Promise.all([
          loadPersonalDamperThresholds(uid),
          loadDamperOutcomes(uid, { sinceDays: 90, limit: 200 }),
        ]);
        if (shouldRecalibrate(current, outcomes)) {
          const fresh = calibrateUserThresholds(outcomes);
          await savePersonalDamperThresholds(uid, fresh);
          console.log(`[damperCalibration] recalibrated thresholds — sample ${fresh.sampleSize}, confidence ${fresh.confidence.toFixed(2)}`);
        }
      } catch { /* non-critical telemetry */ }
    })();
  }

  // Plateau intervention reconciliation — detect any fresh plateaus
  // from accumulated history and turn them into ACTIVE INTERVENTIONS
  // that the next workout generator will apply (modify weight / reps /
  // sets / tempo / swap exercise). The previous architecture surfaced
  // interventions as text only; this closes the loop. Fire-and-forget;
  // non-critical to the save itself.
  if (log.sessionType === 'pt') {
    (async () => {
      try {
        const uid = getCurrentUserId();
        if (!uid) return;
        const { detectPlateaus } = await import('./plateauDetection');
        const { reconcileInterventions } = await import('./plateauInterventions');
        // Use a small recent window — plateaus are about the last 90d.
        const sessions = await getSessionHistory({ since: new Date(Date.now() - 90 * 86_400_000) });
        const plateaus = await detectPlateaus(sessions);
        const active = plateaus.filter(p => p.plateauDetected).map(p => ({
          exerciseName: p.exerciseName,
          topCause: p.likelyCauses[0]?.cause as any ?? 'need_variation',
          sessionsCount: 0,
        }));
        if (active.length === 0) return;
        const map = await reconcileInterventions(uid, active);
        const count = Object.keys(map).length;
        if (count > 0) {
          console.log(`[plateauInterventions] reconciled — ${count} active intervention(s)`);
        }
      } catch { /* non-critical */ }
    })();
  }

  try {
    // Get current user ID (anonymous auth)
    let userId = getCurrentUserId();
    
    // If no user ID, try to wait for auth to be ready (up to 2 seconds)
    if (!userId) {
      console.log('⏳ Waiting for auth to be ready...');
      await new Promise(resolve => setTimeout(resolve, 500));
      userId = getCurrentUserId();
      
      if (!userId) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        userId = getCurrentUserId();
      }
    }
    
    if (!userId) {
      console.warn(' No user ID available after waiting, using anonymous');
    }
    
    // Use 'pending_auth' instead of 'anonymous' to avoid orphaned Firestore sessions
    const effectiveUserId = userId || 'pending_auth';
    // Stamp totalVolume HERE, on the object that goes to BOTH stores.
    // db.saveSession() computed it on its own copy, so the Firestore write —
    // which receives this object, not the Dexie one — carried no volume at
    // all: 218 of 229 production sessions reached the cloud without it.
    const sessionWithUser = stampTotalVolume({ ...log, userId: effectiveUserId } as any) as typeof log & { userId: string };

    // Save to Dexie (IndexedDB) first (immediate, for offline)
    try {
      const { db } = await import('./db');
      const { saveSession: dbSaveSession } = await import('./db');

      const dbSession = {
        ...sessionWithUser,
        userId: sessionWithUser.userId,   //  Primary field (normalized)
        oderId: sessionWithUser.userId,   // Backwards compat
        syncStatus: 'local' as const,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await dbSaveSession(dbSession);
      console.log('Session saved to Dexie:', sessionWithUser.id);
      savedLocally = true;

      // Invalidate caches so next query gets fresh data
      invalidateSessionCache();
      try {
        const { invalidateIntelligenceCache } = await import('./unifiedIntelligence');
        invalidateIntelligenceCache();
      } catch { /* non-critical */ }

      //  INTEL SYSTEM: Award Intel immediately after local save (works offline)
      if (userId && userId !== 'anonymous') {
        try {
          const result = await awardIntelForSession(userId, sessionWithUser);
          if (result.awarded) {
            console.log(` Awarded ${result.intelGained} Intel for session ${sessionWithUser.id}`);
            // Store impact for dashboard to pick up on return
            try {
              storage.set('postWorkoutImpact', JSON.stringify({
                intelGained: result.intelGained,
                leveledUp: result.leveledUp || false,
                newLevel: result.newLevel,
                newStreak: result.newStreak,
                breakdown: result.breakdown,
                mysteryDrop: result.breakdown?.mysteryDrop || 0,
                timestamp: Date.now(),
              }));
            } catch { /* non-critical */ }
          }
        } catch (intelError) {
          console.warn(' Intel award failed (non-critical):', intelError);
        }

        //  ARCHETYPE SYSTEM: Update archetype progress after session
        try {
          const { updateArchetypeProgress } = await import('./archetypeSystem');
          const operatorScore = sessionWithUser.readiness || 0; // Use readiness as proxy for operator score
          const archetypeResult = await updateArchetypeProgress(userId, sessionWithUser, operatorScore);
          if (archetypeResult.unlocked.length > 0) {
            console.log(` ARCHETYPE UNLOCKED: ${archetypeResult.unlocked.join(', ')}`);
            // Dispatch custom event for unlock ceremony UI
            if (typeof window !== 'undefined') {
              const event = new CustomEvent('archetypeUnlocked', {
                detail: { unlocked: archetypeResult.unlocked }
              });
              window.dispatchEvent(event);
            }
          }
        } catch (archetypeError) {
          console.warn(' Archetype progress update failed (non-critical):', archetypeError);
        }

        // OPERATION SPEARHEAD: Update territory control after session.
        // processSessionForSpearhead internally gates on SPEARHEAD_ENABLED,
        // so this is safe to call always — it returns null when the
        // operation is inactive. Live as of v1.9.18.
        try {
          const { processSessionForSpearhead } = await import('./spearheadEngine');
          const spearheadImpact = await processSessionForSpearhead(userId, sessionWithUser);
          if (spearheadImpact && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('spearheadTerritoryGain', {
              detail: spearheadImpact,
            }));
          }
        } catch (spearheadError) {
          console.warn('Spearhead update failed (non-critical):', spearheadError);
        }

        // A session was just logged — cancel any pending "Mission Incomplete"
        // evening reminder that was scheduled this morning, so it doesn't fire
        // despite the user having trained. (The dashboard is the only other
        // place that cancels it, and users often don't reopen it post-workout.)
        try {
          const { cancelMissedSessionReminder } = await import('./missedSessionReminder');
          await cancelMissedSessionReminder();
        } catch { /* non-critical — native-only, safe to ignore */ }
      }
    } catch (localError) {
      console.error('Error saving to Dexie:', localError);
      error = 'Failed to save locally';
      // Emergency fallback: save to localStorage so data isn't lost
      try {
        const emergencyKey = 'emergencySessionBackup';
        const existing = storage.get(emergencyKey);
        const queue: any[] = existing ? JSON.parse(existing) : [];
        if (!queue.some((s: any) => s.id === sessionWithUser.id)) {
          queue.push(sessionWithUser);
          storage.set(emergencyKey, JSON.stringify(queue));
          console.log('Session saved to localStorage emergency backup:', sessionWithUser.id);
          savedLocally = true; // Treat emergency localStorage as "saved locally"
        }
      } catch (emergencyError) {
        console.error('Emergency localStorage backup also failed:', emergencyError);
      }
    }
    
    // Sync to Firebase (primary storage) - skip for pending_auth and anonymous users
    if (userId && userId !== 'anonymous' && userId !== 'pending_auth') {
      try {
        await saveSessionToFirebase(sessionWithUser);
        console.log('� Session synced to Firebase:', sessionWithUser.id);
        savedToCloud = true;

        //  FIX: Update Dexie syncStatus after successful Firebase save
        try {
          const { db: dexieDb, markSynced } = await import('./db');
          await markSynced('sessions', [sessionWithUser.id]);
        } catch (syncStatusErr) {
          console.warn(' Could not update Dexie syncStatus (non-fatal):', syncStatusErr);
        }

        //  COMMUNITY OPS: emit contribution event (idempotent)
        try {
          await submitActiveOperationContribution(sessionWithUser);
          console.log('� Operation contribution submitted:', sessionWithUser.id);
        } catch (contribErr) {
          // Non-fatal; do not fail session save
          console.warn(' Operation contribution submit failed (non-fatal):', contribErr);
        }

        //  UPDATE USER INTELLIGENCE PROFILE
        try {
          await updateUserIntelligenceProfile(userId, sessionWithUser);
        } catch (profileError) {
          console.error(' Error updating user profile (non-critical):', profileError);
          // Don't fail session save if profile update fails
        }

        // FREE-PREVIEW COUNTER. Increments users/{uid}.freeWorkoutsUsed
        // by 1 every time a non-Pro user finishes a *training* session
        // (sessionType !== 'assessment'). Assessment days are calibration
        // — testing day 1 of a program is always free, the user's first
        // real training day is the preview, and from there the paywall
        // kicks in. Pro / admin / beta users skip this entirely so their
        // counter stays at 0 and a downgrade later still lets them
        // taste the product fresh.
        if (sessionWithUser.sessionType !== 'assessment') {
          try {
            const { doc, getDoc, updateDoc, increment } = await import('firebase/firestore');
            const { db: firestoreDb } = await import('./firebase');
            const userRef = doc(firestoreDb, 'users', userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const data = userSnap.data();
              const isPro = data?.role === 'admin' || data?.isBetaTester === true;
              // Also skip RevenueCat-entitled users so a future lapse lets
              // them taste the product fresh (matches the documented intent
              // in the comment block — was previously only skipping
              // admin/beta which meant Pro subscribers burned their
              // preview during their trial).
              let isRcEntitled = false;
              if (!isPro) {
                try {
                  const { hasProAccess } = await import('./revenuecat');
                  isRcEntitled = await hasProAccess();
                } catch { /* non-fatal */ }
              }
              if (!isPro && !isRcEntitled) {
                await updateDoc(userRef, { freeWorkoutsUsed: increment(1) });
              }
            }
          } catch (counterErr) {
            console.warn(' Free-preview counter update failed (non-critical):', counterErr);
          }
        }
      } catch (firebaseError) {
        console.error(' Error saving to Firebase:', firebaseError);
        error = error ? `${error}. Cloud sync failed` : 'Cloud sync failed (saved locally)';
        
        // Queue for retry later
        queueForRetry(sessionWithUser);
      }
    } else {
      console.warn(' Session not synced to Firebase (no user ID) - queued for retry');
      queueForRetry(sessionWithUser);
    }
    
    // Final cache invalidation after all writes (Dexie + Firebase) complete.
    // The earlier invalidation at Dexie-save time covers immediate reads,
    // but Firebase sync may update syncStatus, so invalidate again.
    invalidateSessionCache();

    return { savedLocally, savedToCloud, error };
  } catch (err) {
    console.error(' Error saving session:', err);
    // Invalidate cache even on error — partial writes may have occurred
    invalidateSessionCache();
    return { savedLocally, savedToCloud, error: 'Failed to save session' };
  }
}

/**
 * Queue session for retry when back online or auth is ready
 */
function queueForRetry(session: SessionLog): void {
  try {
    const queueKey = 'pendingFirebaseSessions';
    const existing = storage.get(queueKey);
    const queue: SessionLog[] = existing ? JSON.parse(existing) : [];

    // Don't add duplicates
    if (!queue.some(s => s.id === session.id)) {
      queue.push(session);
      storage.set(queueKey, JSON.stringify(queue));
      console.log('� Session queued for Firebase retry:', session.id);
    }
  } catch (e) {
    console.error('Failed to queue session for retry:', e);
  }
}

/**
 * Retry pending Firebase syncs (call on app init or when auth is ready)
 */
export async function retryPendingFirebaseSyncs(): Promise<number> {
  const queueKey = 'pendingFirebaseSessions';
  const userId = getCurrentUserId();
  
  if (!userId || userId === 'anonymous') {
    return 0;
  }
  
  try {
    const existing = storage.get(queueKey);
    if (!existing) return 0;
    
    const queue: SessionLog[] = JSON.parse(existing);
    let synced = 0;
    const remaining: SessionLog[] = [];
    
    for (const session of queue) {
      try {
        const sessionWithUser = { ...session, userId };
        await saveSessionToFirebase(sessionWithUser);
        console.log(' Retried session synced to Firebase:', session.id);
        synced++;
      } catch (e) {
        console.error(' Retry failed for session:', session.id, e);
        remaining.push(session);
      }
    }
    
    // Update queue with remaining items
    if (remaining.length > 0) {
      storage.set(queueKey, JSON.stringify(remaining));
    } else {
      storage.remove(queueKey);
    }
    
    return synced;
  } catch (e) {
    console.error('Error retrying pending syncs:', e);
    return 0;
  }
}

// ============================================================================
// ACTIVE WORKOUT CLOUD BACKUP (Tier 2 - Firestore)
// ============================================================================

let _activeWorkoutCloudTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Save active workout state to Firestore (debounced - every 60s + on visibility change).
 * Cross-device recovery: "You have an unfinished workout from another device."
 */
export async function saveActiveWorkoutToCloud(userId: string, data: Record<string, any>): Promise<void> {
  try {
    const { getFirestore, doc, setDoc } = await import('firebase/firestore');
    const { getApp } = await import('firebase/app');
    const firestore = getFirestore(getApp());
    const docRef = doc(firestore, 'users', userId, 'activeWorkout', 'current');
    await setDoc(docRef, {
      ...data,
      userId,
      lastSaved: new Date().toISOString(),
      updatedAt: new Date(),
    }, { merge: true });
    console.log('Active workout backed up to Firestore');
  } catch (error) {
    console.warn('Failed to save active workout to Firestore (non-critical):', error);
  }
}

/**
 * Debounced cloud save - batches saves to every 60 seconds.
 */
export function saveActiveWorkoutToCloudDebounced(userId: string, data: Record<string, any>): void {
  if (_activeWorkoutCloudTimer) {
    clearTimeout(_activeWorkoutCloudTimer);
  }
  _activeWorkoutCloudTimer = setTimeout(() => {
    saveActiveWorkoutToCloud(userId, data);
    _activeWorkoutCloudTimer = null;
  }, 60000);
}

/**
 * Load active workout from Firestore (cross-device recovery check on mount).
 */
export async function loadActiveWorkoutFromCloud(userId: string): Promise<Record<string, any> | null> {
  try {
    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
    const { getApp } = await import('firebase/app');
    const firestore = getFirestore(getApp());
    const docRef = doc(firestore, 'users', userId, 'activeWorkout', 'current');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      // Only return if the backup is less than 24 hours old
      const lastSaved = new Date(data.lastSaved);
      const hoursSince = (Date.now() - lastSaved.getTime()) / (1000 * 60 * 60);
      if (hoursSince < 24) {
        return data;
      }
      // Stale - clean up
      await clearActiveWorkoutFromCloud(userId);
    }
    return null;
  } catch (error) {
    console.warn('Failed to load active workout from Firestore:', error);
    return null;
  }
}

/**
 * Clear active workout from Firestore after successful final save.
 */
export async function clearActiveWorkoutFromCloud(userId: string): Promise<void> {
  try {
    const { getFirestore, doc, deleteDoc } = await import('firebase/firestore');
    const { getApp } = await import('firebase/app');
    const firestore = getFirestore(getApp());
    const docRef = doc(firestore, 'users', userId, 'activeWorkout', 'current');
    await deleteDoc(docRef);
    console.log('Active workout cleared from Firestore');
  } catch (error) {
    console.warn('Failed to clear active workout from Firestore (non-critical):', error);
  }
}

/**
 * Flush any pending cloud save immediately (call on visibility change).
 */
export function flushActiveWorkoutCloudSave(userId: string, data: Record<string, any>): void {
  if (_activeWorkoutCloudTimer) {
    clearTimeout(_activeWorkoutCloudTimer);
    _activeWorkoutCloudTimer = null;
  }
  saveActiveWorkoutToCloud(userId, data);
}

/**
 * Cancel any pending debounced cloud save without firing it. Call this
 * before clearActiveWorkoutFromCloud — otherwise the timer will fire 60s
 * later and re-create the doc we just deleted, making the discard look
 * broken to the user. Was also responsible for spurious 401s when the
 * timer fired against an expired auth token after the user moved on.
 */
export function cancelActiveWorkoutCloudSave(): void {
  if (_activeWorkoutCloudTimer) {
    clearTimeout(_activeWorkoutCloudTimer);
    _activeWorkoutCloudTimer = null;
  }
}

/**
 * Get session history from Dexie (async)
 *
 *  BREAKING CHANGE: This function is now async.
 * All callers must use `await getSessionHistory()` or `getSessionHistory().then()`
 */
/**
 * A session's duration in SECONDS, whatever type it is.
 *
 * `duration` carries seconds for PT/assessment/sprint and MINUTES for Range
 * (see BaseSessionLog.duration). Anything summing or formatting across session
 * types must go through here — reading the raw field renders a 45-minute range
 * session as 45 seconds.
 */
export function sessionDurationSeconds(session: { sessionType?: string; duration?: number }): number {
  const d = session.duration ?? 0;
  if (d <= 0) return 0;
  return session.sessionType === 'range' ? d * 60 : d;
}

export async function getSessionHistory(options?: {
  limit?: number;
  programSelection?: string;
  since?: Date;
  all?: boolean;
}): Promise<SessionLog[]> {
  // SSR safety check
  if (typeof window === 'undefined') {
    console.log('📋 getSessionHistory: SSR environment, returning empty array');
    return [];
  }

  try {
    const { db } = await import('./db');
    const userId = getCurrentUserId();

    if (!userId) {
      console.log('📋 getSessionHistory: No user ID, returning empty array');
      return [];
    }

    // Default to 90-day window unless `all` is set or a specific `since` is provided
    const effectiveSince = options?.all
      ? new Date(0)
      : (options?.since ?? new Date(Date.now() - 90 * 86400000));

    // Check cache - serves sub-range queries from cached data
    const now = Date.now();
    const cacheValid =
      sessionCache.data !== null &&
      sessionCache.userId === userId &&
      (now - sessionCache.timestamp) < SESSION_CACHE_TTL &&
      sessionCache.since !== null &&
      sessionCache.since.getTime() <= effectiveSince.getTime();

    let result: SessionLog[];

    if (cacheValid) {
      // Cache covers this date range — filter from cached data
      result = sessionCache.data!;

      // If caller wants a narrower range than what's cached, filter
      if (effectiveSince.getTime() > sessionCache.since!.getTime()) {
        result = result.filter(s => new Date(s.date) >= effectiveSince);
      }

      // Apply programSelection filter from cache
      if (options?.programSelection) {
        result = result.filter(s => s.programSelection === options.programSelection);
      }

      // Apply limit from cache
      if (options?.limit) {
        result = result.slice(0, options.limit);
      }

      console.log(` getSessionHistory: Cache hit (${result.length} sessions)`);
      return result;
    }

    // Fetch from Dexie using compound index [userId+date]
    let sessions: DBSession[] = await db.sessions
      .where('[userId+date]')
      .between([userId, effectiveSince], [userId, new Date('2099-12-31')], true, true)
      .toArray();

    // Filter by program if specified
    if (options?.programSelection) {
      sessions = sessions.filter(s => s.programSelection === options.programSelection);
    }

    // Sort by date ascending (oldest first) using actual Date comparison
    // Many functions use .slice(-N) to get recent sessions, expecting oldest-first order
    sessions.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB; // Ascending (oldest first)
    });

    if (options?.limit) {
      sessions = sessions.slice(0, options.limit);
    }

    console.log(` getSessionHistory: Loaded ${sessions.length} sessions from Dexie (since ${effectiveSince.toLocaleDateString()})`);

    // Map DBSession to SessionLog (oderId → userId)
    result = sessions.map(s => ({
      ...s,
      userId: s.userId || s.oderId,  // Support legacy oderId for backwards compat
      exercises: s.exercises || [],
    })) as SessionLog[];

    // Update cache for non-limited, non-program-filtered queries
    if (!options?.programSelection && !options?.limit) {
      sessionCache.data = result;
      sessionCache.userId = userId;
      sessionCache.since = effectiveSince;
      sessionCache.timestamp = now;
    }

    return result;
  } catch (error) {
    console.error(' getSessionHistory: Error loading from Dexie', error);
    return [];
  }
}

/**
 * Sync session history from Firebase to Dexie
 *  REFACTORED: Now uses Dexie instead of localStorage
 * Call this on app initialization
 */
export async function syncSessionHistoryFromFirebase(): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId) {
    console.log('� No user ID, skipping Firebase sync');
    return 0;
  }

  try {
    console.log('� Syncing sessions from Firebase to Dexie...');
    const { forceFullDownloadFromFirebase } = await import('./dexieFirebaseSync');
    const stats = await forceFullDownloadFromFirebase(userId);
    console.log(` Synced ${stats.sessions} sessions from Firebase to Dexie`);
    return stats.sessions;
  } catch (error) {
    console.error(' Failed to sync sessions from Firebase:', error);
    return 0;
  }
}

/**
 * Get sessions for a specific exercise (PT sessions only)
 * Now supports superset exercises by extracting data from superset rounds
 * 
 * ENHANCED: Uses alias resolution to match exercises across name variations
 * e.g., "Tricep Pulldown" and "Tricep Pushdown" will be matched as the same exercise
 */
export async function getExerciseHistory(exerciseName: string): Promise<ExerciseLog[]> {
  const sessions = await getSessionHistory();
  
  // Resolve the exercise name to its canonical form, then normalize
  const resolvedName = resolveExerciseName(exerciseName);
  const normalizedName = resolvedName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  const originalNormalized = exerciseName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  
  const history: ExerciseLog[] = [];
  
  sessions
    .filter((s): s is PTSessionLog => s.sessionType === 'pt' || s.sessionType === 'assessment')
    .forEach(session => {
      session.exercises.forEach(ex => {
        // Also resolve the stored exercise name to its canonical form
        const exResolvedName = resolveExerciseName(ex.exerciseName);
        const exNormalizedName = exResolvedName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
        const exOriginalNormalized = ex.exerciseName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
        
        // Check if this is a match (direct, canonical, or either original)
        const isMatch = exNormalizedName === normalizedName || 
                        exOriginalNormalized === originalNormalized ||
                        exNormalizedName === originalNormalized ||
                        exOriginalNormalized === normalizedName;
        
        if (isMatch) {
          history.push(ex);
        } else if (ex.supersetRounds && Array.isArray(ex.supersetRounds)) {
          // Check if this exercise is within a superset
          for (const round of ex.supersetRounds) {
            if (round.exercises && Array.isArray(round.exercises)) {
              for (const supersetEx of round.exercises) {
                const supersetResolved = resolveExerciseName(supersetEx.name);
                const supersetNormalized = supersetResolved.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
                const supersetOriginal = supersetEx.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
                
                const supersetMatch = supersetNormalized === normalizedName ||
                                       supersetOriginal === originalNormalized ||
                                       supersetNormalized === originalNormalized ||
                                       supersetOriginal === normalizedName;
                
                if (supersetMatch) {
                  // Found the exercise in a superset - create a synthetic ExerciseLog
                  // Use the best set from all rounds
                  const bestSet = findBestSetFromSuperset(ex.supersetRounds, supersetEx.name);
                  if (bestSet) {
                    history.push({
                      exerciseName: supersetEx.name,
                      exerciseType: 'weight',
                      block: ex.block || '',
                      format: ex.format || '',
                      targetSets: ex.targetSets || '',
                      targetLoad: bestSet.weight,
                      targetRPE: ex.targetRPE || '',
                      restPeriod: ex.restPeriod || '',
                      completed: true,
                      sets: [{
                        weight: bestSet.weight,
                        reps: bestSet.reps,
                        rpe: bestSet.rpe,
                        completed: true,
                      }],
                      actualLoad: bestSet.weight,
                      actualReps: bestSet.reps,
                      actualRPE: bestSet.rpe,
                    });
                  }
                  break;
                }
              }
            }
          }
        }
      });
    });
  
  return history;
}

/**
 * Helper function to find the best set from superset rounds for a specific exercise
 */
function findBestSetFromSuperset(
  rounds: any[],
  exerciseName: string
): { weight: number; reps: number; rpe: number } | null {
  let bestSet: { weight: number; reps: number; rpe: number } | null = null;
  let bestVolume = 0;
  
  // Use alias resolution for matching
  const resolvedName = resolveExerciseName(exerciseName);
  const normalizedName = resolvedName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  const originalNormalized = exerciseName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  
  for (const round of rounds) {
    if (round.exercises && Array.isArray(round.exercises)) {
      for (const ex of round.exercises) {
        const exResolved = resolveExerciseName(ex.name);
        const exNormalized = exResolved.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
        const exOriginal = ex.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
        
        const isMatch = exNormalized === normalizedName || 
                        exOriginal === originalNormalized ||
                        exNormalized === originalNormalized ||
                        exOriginal === normalizedName;
        
        if (isMatch && ex.weight > 0 && ex.reps > 0 && ex.rpe) {
          const volume = ex.weight * ex.reps;
          if (volume > bestVolume) {
            bestVolume = volume;
            bestSet = { weight: ex.weight, reps: ex.reps, rpe: ex.rpe };
          }
        }
      }
    }
  }
  
  return bestSet;
}

/**
 * Get only PT sessions
 * @param filterByCurrentProgram - If true, only return sessions for the current program
 */
export async function getPTSessionHistory(filterByCurrentProgram: boolean = false): Promise<PTSessionLog[]> {
  const sessions = await getSessionHistory();
  let filtered = sessions.filter((s): s is PTSessionLog => s.sessionType === 'pt' || s.sessionType === 'assessment');
  
  if (filterByCurrentProgram && typeof window !== 'undefined') {
    try {
      const stored = storage.get('userProgramSelection');
      if (stored) {
        const selection = JSON.parse(stored);
        const currentProgramId = selection.programId;
        filtered = filtered.filter(session => {
          if (!session.programSelection) {
            return !currentProgramId; // Show legacy data only if no current program
          }
          return session.programSelection === currentProgramId;
        });
      }
    } catch (e) {
      console.warn(' Could not load program selection for PT session filtering');
    }
  }
  
  return filtered;
}

/**
 * Get only Range sessions
 * @param filterByCurrentProgram - If true, only return sessions for the current program
 */
export async function getRangeSessionHistory(filterByCurrentProgram: boolean = false): Promise<RangeSession[]> {
  const sessions = await getSessionHistory();
  let filtered = sessions.filter((s): s is RangeSession => s.sessionType === 'range');
  
  if (filterByCurrentProgram && typeof window !== 'undefined') {
    try {
      const stored = storage.get('userProgramSelection');
      if (stored) {
        const selection = JSON.parse(stored);
        const currentProgramId = selection.programId;
        filtered = filtered.filter(session => {
          if (!session.programSelection) {
            return !currentProgramId; // Show legacy data only if no current program
          }
          return session.programSelection === currentProgramId;
        });
      }
    } catch (e) {
      console.warn(' Could not load program selection for Range session filtering');
    }
  }
  
  return filtered;
}

/**
 * Save a range session
 *  REFACTORED: Now saves directly to Dexie (no localStorage)
 */
export async function saveRangeSession(session: RangeSession): Promise<void> {
  const userId = getCurrentUserId();
  
  // Save to Dexie
  const dbSession: DBSession = {
    ...session,
    userId: userId || 'anonymous',   //  Primary field (normalized)
    oderId: userId || 'anonymous',   // Backwards compat
    syncStatus: 'local',
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  await db.sessions.put(dbSession);
  console.log(` Range session saved to Dexie: ${session.protocolName} (${session.totalRounds} rounds)`);

  // Sync to Firebase
  if (userId) {
    await withAuth(() => saveSessionToFirebase(session));
  }
}

/**
 * Update User Intelligence Profile after session
 * This runs asynchronously and doesn't block session save
 */
async function updateUserIntelligenceProfile(userId: string, session: SessionLog): Promise<void> {
  try {
    console.log('� Updating user intelligence profile...');
    
    // Get current profile or create a new one
    let profile = await getUserProfile(userId);

    // If no profile exists, create a default one
    if (!profile) {
      console.log('ℹ No profile found - creating default profile');
      profile = createDefaultProfile(userId);
      await saveUserProfile(profile);
      console.log('✓ Default profile created for', userId);
    }
    
    // Update fitness-fatigue model
    const updatedFitnessModel = updateFitnessFatigue(profile.fitnessModel, session);
    
    // Get all sessions for full profile analysis
    const allSessions = await getSessionHistory({ all: true });
    
    // Check if we should run full profile analysis
    const shouldAnalyze = allSessions.length % 5 === 0; // Every 5 sessions
    
    if (shouldAnalyze && allSessions.length >= 3) {
      console.log('� Running full profile analysis...');
      
      // Run learning algorithms
      const analysis = await analyzeUserProfile(profile, allSessions);
      
      // Update profile with analysis results
      await updateUserProfile(userId, {
        ...analysis.profileUpdates,
        fitnessModel: updatedFitnessModel,
        lastAnalysisDate: new Date(),
        nextScheduledAnalysis: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week
      });
      
      console.log(' Profile updated with full analysis');
      console.log('� Insights:', analysis.insights.length);
      console.log('� Recommendations:', analysis.recommendations.length);
      console.log('� Confidence:', (analysis.confidenceScore * 100).toFixed(0) + '%');
    } else {
      // Just update fitness-fatigue model
      await updateUserProfile(userId, {
        fitnessModel: updatedFitnessModel,
      });
      
      console.log(' Fitness-fatigue model updated');
    }
  } catch (error) {
    console.error(' Error updating user intelligence profile:', error);
    // Don't throw - this is non-critical
  }
}

/**
 * Get range session statistics
 */
export async function getRangeStats() {
  const rangeSessions = await getRangeSessionHistory();
  
  if (rangeSessions.length === 0) {
    return null;
  }

  const totalRounds = rangeSessions.reduce((sum, s) => sum + s.totalRounds, 0);
  const avgAccuracy = rangeSessions.reduce((sum, s) => sum + s.overallAccuracy, 0) / rangeSessions.length;
  const totalCost = rangeSessions.reduce((sum, s) => sum + (s.cost || 0), 0);
  
  // Get all PRs
  const allPRs = rangeSessions.flatMap(s => s.highlights.newPRs);
  
  // Get most recent session
  const mostRecent = rangeSessions.sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )[0];

  return {
    totalSessions: rangeSessions.length,
    totalRounds,
    avgAccuracy,
    totalCost,
    allPRs,
    mostRecent
  };
}

